import { clsx, type ClassValue } from 'clsx';

/** Tailwind class merger — wraps clsx, adds nothing fancy. */
export function cn(...inputs: ClassValue[]): string {
  return clsx(inputs);
}

/** Trigger a haptic vibration if supported (Android). */
export function haptic(pattern: number | number[] = 12): void {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(pattern);
    } catch {
      /* noop */
    }
  }
}

/** Format a number with French thousand separators. */
export function fmt(n: number): string {
  return n.toLocaleString('fr-FR').replace(/,/g, ' ');
}

/** Compute initials (max 2 chars) from a full name. */
export function initials(name?: string | null): string {
  if (!name || typeof name !== 'string') return '?';
  const cleaned = name.trim();
  if (!cleaned) return '?';
  return cleaned
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// Libellé de deadline ancré sur le fuseau métier (ADR 0004 D2) : l'affichage
// vient de l'ISO serveur, jamais d'un calcul local de règle.
// Fuseau métier = heure de Cotonou, identifiant IANA `Africa/Porto-Novo`
// (« Africa/Cotonou » n'existe pas dans la base tz — correctif 2026-07-19,
// voir ADR 0004 : posé tel quel, `Intl.DateTimeFormat` LÈVE, ce qui plantait
// le rendu de la page d'accueil).
export function formatDeadline(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Africa/Porto-Novo',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

// Heure locale (Africa/Porto-Novo) d'un timestamp ISO — ex. « 19h32 ».
// Même fuseau métier que `formatDeadline` (ADR 0004 D2), format « Xh XX »
// (kit v2 §7 : « heures locales (19h, midi) », pas de deux-points).
export function formatHeure(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Africa/Porto-Novo',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(d)
    .replace(':', 'h');
}

// Convertit les séparateurs '+' saisis par l'admin (ex. seed.ts : « Riz
// sauce arachide + Poulet braisé ») en interponts '·' (kit v2 §2 : « les
// titres de plats séparent leurs composants par des interponts »). Ne
// fabrique jamais d'interpont sur un nom sans composant multiple.
export function withInterponts(nomPlat: string): string {
  return nomPlat.replace(/\s*\+\s*/g, ' · ');
}

/** Convert a base64 VAPID key to a Uint8Array (browser PushManager format). */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Clé d'idempotence de commande (Frappe 3) : uuid natif quand disponible,
// repli maison sinon (wrap-ready ADR 0007 D3 — vieux WebView sans
// crypto.randomUUID). L'unicité côté serveur est par (userId, clé) : le
// repli n'a besoin que d'être improbable, pas cryptographique.
export function genererIdRequete(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* repli ci-dessous */
  }
  return 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () =>
    Math.floor(Math.random() * 16).toString(16),
  );
}

// Date LOCALE au format YYYY-MM-DD — jamais via toISOString() (UTC) : entre
// 00h et 01h à Porto-Novo (UTC+1), l'ISO recule d'un jour et le « demain »
// du formulaire admin proposait aujourd'hui (baptême du feu, Gardien).
export function dateLocaleYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const j = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${j}`;
}

// ── Accueil (planches 5a → 5e) — tout en heure de Cotonou ──
const TZ = 'Africa/Porto-Novo';

/** Jour civil à Cotonou, « YYYY-MM-DD » (en-CA donne cet ordre nativement). */
export function jourCivil(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** Pastille de l'affiche (artboard 5a/5d) : « DEMAIN » ou le jour de livraison, en majuscules. */
export function libelleJourLivraison(isoLivraison: string, now: Date): string {
  const livraison = new Date(isoLivraison);
  if (Number.isNaN(livraison.getTime())) return '';
  const demain = new Date(now.getTime() + 24 * 3_600_000);
  if (jourCivil(livraison) === jourCivil(demain)) return 'DEMAIN';
  return new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, weekday: 'long' }).format(livraison).toUpperCase();
}

/** « 21h05 » le même jour, « jeu. 21h05 » sinon (artboard 5c : « Commande de jeu. 21h05 »). */
export function formatJourHeure(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const heure = formatHeure(iso);
  if (jourCivil(d) === jourCivil(now)) return heure;
  const jour = new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, weekday: 'short' }).format(d);
  return `${jour} ${heure}`;
}

/** « 22h », ou « 22h30 » si les minutes ne sont pas nulles. */
export function formatHeureCourte(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, hour: '2-digit', minute: '2-digit' }).formatToParts(d);
  const h = parts.find((p) => p.type === 'hour')?.value ?? '';
  const m = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return m === '00' ? `${h}h` : `${h}h${m}`;
}

/** « ven. 22h » (ligne « ⏳ Commande avant ven. 22h », artboard 5a). */
export function formatDeadlineCourte(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const jour = new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, weekday: 'short' }).format(d);
  return `${jour} ${formatHeureCourte(iso)}`;
}

/** Le prochain 19h de Cotonou (UTC+1 fixe → 18h UTC). Rituel de marque, pas une règle serveur. */
export function prochain19h(now: Date): Date {
  const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 18, 0, 0));
  if (target.getTime() <= now.getTime()) target.setUTCDate(target.getUTCDate() + 1);
  return target;
}

/**
 * La promesse « le menu arrive à 19h » ne doit jamais mentir (vision produit :
 * le chiffre vrai). Lundi → jeudi : la copy de l'artboard ; vendredi : rien ne
 * se livre demain mais le prochain menu arrive bien à 19h ; samedi et
 * dimanche : la cuisine souffle, aucune promesse horaire, pas de compte à
 * rebours. C'est de la copy, pas un état : l'horloge locale suffit.
 */
export function promesseProchainMenu(now: Date): { compteARebours: boolean; titre: string; piedRemis: string } {
  const jour = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short' }).format(now);
  if (jour === 'Sat' || jour === 'Sun') {
    return { compteARebours: false, titre: "C'est le week-end, la cuisine souffle.", piedRemis: 'Bon appétit. On se retrouve lundi.' };
  }
  if (jour === 'Fri') {
    return { compteARebours: true, titre: 'Le prochain menu arrive à 19h 🍲', piedRemis: 'Bon appétit. On se retrouve à 19h pour le prochain menu.' };
  }
  return { compteARebours: true, titre: 'Le menu de demain arrive à 19h 🍲', piedRemis: 'Bon appétit. On se retrouve à 19h pour le menu de demain.' };
}

/** HH:MM:SS jusqu'au prochain 19h de Cotonou. */
export function compteARebours19h(now: Date): string {
  const target = prochain19h(now);
  const diff = Math.floor((target.getTime() - now.getTime()) / 1000);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return [h, m, s].map((n) => n.toString().padStart(2, '0')).join(':');
}

/** « 1 500 F » : espace simple entre les milliers, « F » jamais « FCFA » (kit). */
export function formatFrancs(n: number): string {
  return `${new Intl.NumberFormat('fr-FR').format(n).replace(/[\u202f\u00a0]/g, ' ')} F`;
}
